import { consumeSourceCardForModel, consumeSourceStyleForModel } from './sourceCardTracker'
import { insufficientText, isInsufficientError, isInsufficientMessage, reportInsufficientCredits } from '../utils/credits'
import { getAttachedStyleCard } from './attachedStyleCard'
import { variantUserText } from '../utils/variantPrompt'
import { getStudioFolder } from './studioFolderTracker'
import { getEditSource } from './editSourceTracker'
import { consumeSceneExtendForModel } from './sceneExtendTracker'
import { getScene } from './sceneSelection'
import { assetIdsInPrompt } from '../prompt-vars/phrases'

export const API_URL =
	import.meta.env.VITE_API_URL ||
	(typeof window !== 'undefined' && window.location.hostname === 'localhost'
		? 'http://localhost:8100'
		: '')

const ERROR_MESSAGES: Record<string, string> = {
	'Not Found': 'Сервис временно недоступен',
	'Unknown error': 'Неизвестная ошибка',
	'Request failed': 'Ошибка запроса',
	'Session expired': 'Сессия истекла',
	'Internal Server Error': 'Нейросеть временно недоступна',
	'Bad Gateway': 'Сервер недоступен',
	'Service Unavailable': 'Сервис недоступен',
	'Gateway Timeout': 'Превышено время ожидания',
	Forbidden: 'Доступ запрещён',
	Unauthorized: 'Необходима авторизация',
	'Too Many Requests': 'Слишком много запросов, попробуйте позже',
	'Invalid credentials': 'Неверный email или пароль',
	'User not found': 'Пользователь не найден',
	'Email already registered': 'Email уже зарегистрирован',
	'Invalid token': 'Недействительный токен',
	'Token expired': 'Токен истёк',
	'Not enough credits': 'Недостаточно кредитов',
	'Invalid email or password': 'Неверный email или пароль',
	'Email not verified': 'Email не подтверждён',
	'User is blocked': 'Аккаунт заблокирован',
	// Аккаунт удалён — повторную регистрацию открывает только поддержка
	// (см. backend services/account_deletion.py). Бэк уже присылает текст
	// по-русски; дублируем, чтобы substring-матч не подменил его чем-то
	// вроде «Доступ запрещён» из Forbidden.
	'Аккаунт был удален, обратитесь в техподдержку':
		'Аккаунт был удален, обратитесь в техподдержку',
	// Turnstile: бэк уже отдаёт текст по-русски (app/api/v1/auth.py,
	// CAPTCHA_FAILED_MESSAGE) — дублируем по той же причине, что и выше.
	'Не удалось подтвердить, что вы не робот. Обновите страницу и попробуйте снова':
		'Не удалось подтвердить, что вы не робот. Обновите страницу и попробуйте снова',
	'Token revoked': 'Сессия завершена — войдите заново',
	'Password too short': 'Пароль слишком короткий',
	'Invalid code': 'Неверный код',
	'Code expired': 'Код истёк',
	'generate task timeout.': 'Нейросеть временно недоступна. Попробуйте позже.',
	'Your request was blocked by our moderation system.':
		'Ваш запрос заблокирован модерацией нейросети.',
	'Video duration must be between 3 and 30 seconds':
		'Длительность видео должна быть от 3 до 30 секунд.',
	'The model name you specified is not supported':
		'Выбранная модель временно недоступна у провайдера.',
	'The input was invalid. Please try again with different inputs.':
		'Некорректные входные данные. Попробуйте другой запрос или изображение.',
}

/**
 * Переводит сообщение об ошибке из ответа API в русскоязычное.
 *
 * Порядок поиска:
 *   1. Если не строка — возвращает дефолтную строку по типу
 *   2. Точное совпадение в ERROR_MESSAGES
 *   3. Case-insensitive substring match
 *   4. Возвращает исходное сообщение
 *
 * Экспортирован для unit-тестов. Также может использоваться где-то ещё
 * если нужна стандартная русификация ошибок от бэка.
 */
export function russifyError(msg: unknown): string {
	if (typeof msg !== 'string') {
		if (Array.isArray(msg)) return 'Некорректные данные запроса'
		if (typeof msg === 'object' && msg !== null) return 'Ошибка сервера'
		return 'Неизвестная ошибка'
	}
	if (ERROR_MESSAGES[msg]) return ERROR_MESSAGES[msg]
	for (const [en, ru] of Object.entries(ERROR_MESSAGES)) {
		if (msg.toLowerCase().includes(en.toLowerCase())) return ru
	}
	return msg
}

interface RequestOptions {
	method?: string
	body?: unknown
	headers?: Record<string, string>
	skipAuth?: boolean
	signal?: AbortSignal
	/** Отказ «не хватает токенов» без стандартного тоста (баланс всё равно перечитывается): место вызова показывает его само. */
	silentInsufficient?: boolean
}

// Эндпоинты запуска генерации — только на них «съедаем» pending source-card
// (и source-style, см. consumeSourceStyleForModel ниже). Покрываем оба пути
// (sync /generate и async /generate-async) для image и video — фронт реально
// использует именно generate-async для видео, без него источник не атрибутировался.
// Альтернативные image-эндпоинты (nano-banana, midjourney, image-to-image)
// принимают source_*_id в body, но их pydantic-схемы пока его не парсят —
// inject безопасен (Pydantic с extra='ignore' молча отбрасывает unknown поля).
function isGenerateEndpoint(endpoint: string): boolean {
	return /\/api\/v1\/(images\/(generate|generate-async|nano-banana|midjourney|image-to-image)|video\/(generate|generate-async|midjourney)|music\/generate-async|presentations\/(generate-async|gamma\/generate-async))/.test(
		endpoint,
	)
}

/** Текст обрыва связи. Экспортирован: страница генерации по нему понимает,
 *  что запрос не дошёл ИЗ-ЗА СЕТИ, и проверяет, не выполнил ли бэк задачу
 *  всё-таки (см. «догон потерянной генерации» в Generate.tsx). */
export const NETWORK_ERROR_MESSAGE = 'Нет связи с сервером — проверьте интернет и попробуйте ещё раз'

/** Обрыв соединения (fetch reject) в человеческом виде: браузеры отдают
 *  «Load failed» / «Failed to fetch» — юзеру это ничего не объясняет
 *  (жалоба владельца 2026-08-01: так выглядела свёрнутая вкладка на телефоне).
 *  Исходную ошибку кладём в cause — для консоли и разбора. */
function networkError(cause: unknown): Error {
	return new Error(NETWORK_ERROR_MESSAGE, { cause })
}

class ApiClient {
	private token: string | null = null
	private refreshToken: string | null = null
	private refreshPromise: Promise<boolean> | null = null

	constructor() {
		this.token = localStorage.getItem('token')
		this.refreshToken = localStorage.getItem('refresh_token')
	}

	setTokens(token: string | null, refreshToken?: string | null) {
		this.token = token
		if (token) {
			localStorage.setItem('token', token)
		} else {
			localStorage.removeItem('token')
		}

		if (refreshToken !== undefined) {
			this.refreshToken = refreshToken
			if (refreshToken) {
				localStorage.setItem('refresh_token', refreshToken)
			} else {
				localStorage.removeItem('refresh_token')
			}
		}
	}

	setToken(token: string | null) {
		this.setTokens(token)
	}

	getToken() {
		return this.token
	}

	isAuthenticated() {
		return !!this.token
	}

	private async tryRefresh(): Promise<boolean> {
		if (!this.refreshToken) return false

		if (this.refreshPromise) {
			return this.refreshPromise
		}

		this.refreshPromise = (async () => {
			try {
				const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ refresh_token: this.refreshToken }),
				})

				if (response.ok) {
					const data = await response.json()
					this.setTokens(data.access_token, data.refresh_token)
					return true
				}
			} catch (e) {
				console.error('Token refresh failed:', e)
			}

			this.setTokens(null, null)
			return false
		})()

		const result = await this.refreshPromise
		this.refreshPromise = null
		return result
	}

	/** Повтор запроса после сетевого обрыва. Зовётся ТОЛЬКО для идемпотентных
	 *  запросов (X-Idempotency-Key) — повтор не создаёт вторую генерацию и не
	 *  списывает второй раз, бэк отдаёт уже созданную задачу.
	 *
	 *  Сначала ждём возвращения вкладки: на телефоне соединение рвётся именно
	 *  потому, что страницу свернули, и повтор «в фоне» упал бы так же. Дальше —
	 *  три попытки с нарастающей паузой; не вышло — отдаём исходную ошибку. */
	private async retryAfterNetworkDrop(doFetch: () => Promise<Response>, original: unknown): Promise<Response> {
		if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
			await new Promise<void>((resolve) => {
				const onVisible = () => {
					if (document.visibilityState !== 'hidden') {
						document.removeEventListener('visibilitychange', onVisible)
						resolve()
					}
				}
				document.addEventListener('visibilitychange', onVisible)
				// Страховка: вкладка может не вернуться — не держим промис вечно.
				setTimeout(() => {
					document.removeEventListener('visibilitychange', onVisible)
					resolve()
				}, 60_000)
			})
		}
		const delays = [800, 2000, 4000]
		for (const wait of delays) {
			await new Promise((r) => setTimeout(r, wait))
			try {
				return await doFetch()
			} catch { /* всё ещё нет сети — пробуем следующую паузу */ }
		}
		throw networkError(original)
	}

	async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
			...options.headers,
		}

		if (this.token && !options.skipAuth) {
			headers['Authorization'] = `Bearer ${this.token}`
		}

		// Точная атрибуция источника генерации: подмешиваем source_card_id
		// (карточка popular_category) и/или source_style_id (StylePreset /
		// тренд) в тело запроса. Обе атрибуции ЛИПКИЕ по модели: держатся на
		// доработках/ре-роллах, снимаются при смене модели (см. sourceCardTracker)
		// — на style-теге ещё и MONEY-PATH floor цены (style price_override).
		// Оба тэга независимы — в одном запросе теоретически могут быть оба, на
		// практике юзер выбирает либо карточку, либо стиль.
		let reqBody = options.body
		if (
			reqBody && typeof reqBody === 'object' && !Array.isArray(reqBody) &&
			isGenerateEndpoint(endpoint)
		) {
			// Карточка атрибутируется пока генерация на её модели (доработки/ре-роллы
			// тоже); смена модели снимает контекст (см. sourceCardTracker). Модель — из тела.
			const bodyModel = (reqBody as Record<string, unknown>).model
			const scid = consumeSourceCardForModel(typeof bodyModel === 'string' ? bodyModel : null)
			if (scid) {
				reqBody = { ...(reqBody as Record<string, unknown>), source_card_id: scid }
				// Промпт карточки — наш, юзеру он не показывается нигде (бэк режет
				// его в /files и /user/history). Но СВОЙ ввод («Свой стиль» в
				// инструменте) юзер должен увидеть в «Моих генерациях» и
				// переиспользовать — поэтому отправляем его отдельным полем.
				// Источник — пин карточки (переживает F5 вместе с customValue),
				// а не локальный стейт GTM: после перезагрузки он пуст.
				// Текст активного ввода: «Свой вариант» — его значение, стиль с полем — значение поля (22.10: раньше
				// уходил только «Свой вариант», и он залипал после выбора обычного стиля; значения полей не уходили).
				const pin = getAttachedStyleCard()
				const own = pin ? variantUserText(pin) : ''
				if (own) reqBody = { ...(reqBody as Record<string, unknown>), user_prompt: own }
			}
			const ssid = consumeSourceStyleForModel(typeof bodyModel === 'string' ? bodyModel : null)
			if (ssid) reqBody = { ...(reqBody as Record<string, unknown>), source_style_id: ssid }
			// Цепочка правок студии: фото взято «в работу» → эта генерация — его
			// правка. Тег ЛИПКИЙ, пока кадр в слоте (ретрай и «Пересоздать» не
			// теряют цепочку); снимает его Generate.tsx по опустевшему слоту.
			const esid = getEditSource()
			if (esid) reqBody = { ...(reqBody as Record<string, unknown>), edit_source_request_id: esid }
			// Продление сцены Omni: к какому ролику Google дописывает хвост. Тег
			// привязан к модели (см. sceneExtendTracker) — на чужой карточке
			// снимается сам, а не уезжает вместе с запросом.
			const xsid = consumeSceneExtendForModel(typeof bodyModel === 'string' ? bodyModel : null)
			if (xsid) reqBody = { ...(reqBody as Record<string, unknown>), extend_source_request_id: xsid }
			// Папка студии: фиксируется бэком НА СТАРТЕ, поэтому переход в другую
			// папку во время генерации не уводит результат — он приезжает туда,
			// откуда его запустили (требование владельца 2026-08-03). Для правки
			// папку бэк унаследует от родителя даже без этого поля.
			const folderCtx = getStudioFolder()
			if (folderCtx.studio) {
				reqBody = {
					...(reqBody as Record<string, unknown>),
					studio: true,
					...(folderCtx.folderId ? { studio_folder_id: folderCtx.folderId } : {}),
				}
			}
			// Сцена (Ф2 «Библиотека сцены»): asset_ids — ассеты, чьи фото РЕАЛЬНО в теле запроса или чьё
			// упоминание есть в промпте; character_id — первый персонаж с фото в теле (юзер мог снять фото из
			// слота — тогда атрибуция была бы враньём). Сверка по path presigned-URL (подпись меняется, путь
			// стабилен). Бэк всё равно оставит только свои или библиотечные (apply_scene_refs).
			const scene = getScene()
			if (scene) {
				const body = reqBody as Record<string, unknown>
				const raw = JSON.stringify(body)
				const inBody = (urls: string[] = []) => urls.some((u) => {
					const p = u.split('?', 1)[0]
					return !!p && raw.includes(p)
				})
				const mentioned = assetIdsInPrompt(`${typeof body.prompt === 'string' ? body.prompt : ''} ${typeof body.user_prompt === 'string' ? body.user_prompt : ''}`)
				const ids = scene.assets.filter((a) => inBody(scene.placedBy[a.id]) || mentioned.includes(a.id)).map((a) => a.id)
				if (ids.length) reqBody = { ...body, asset_ids: ids }
				const hero = scene.assets.find((a) => a.kind === 'character' && inBody(scene.placedBy[a.id]))
				if (hero) reqBody = { ...(reqBody as Record<string, unknown>), character_id: hero.id }
			}
		}

		const doFetch = () =>
			fetch(`${API_URL}${endpoint}`, {
				method: options.method || 'GET',
				headers,
				body: reqBody ? JSON.stringify(reqBody) : undefined,
				signal: options.signal,
			})

		let response: Response
		try {
			response = await doFetch()
		} catch (err) {
			// Уход со вкладки на телефоне рвёт соединение (iOS «Load failed»), и
			// генерация показывалась ошибкой, хотя бэк её принял и выполнил
			// (жалоба владельца 2026-08-01). Повторяем — но ТОЛЬКО запросы с
			// ключом идемпотентности: бэк по нему вернёт УЖЕ созданную задачу, а
			// не спишет второй раз (resolve_generation_idempotency).
			const aborted = options.signal?.aborted || (err as Error)?.name === 'AbortError'
			if (aborted) throw err
			// Неидемпотентный запрос повторять нельзя — но сказать по-человечески
			// можно: «Load failed» из iOS юзеру ничего не объясняет.
			if (!headers['X-Idempotency-Key']) throw networkError(err)
			response = await this.retryAfterNetworkDrop(doFetch, err)
		}

		if (response.status === 401 && !options.skipAuth) {
			const refreshed = await this.tryRefresh()
			if (refreshed) {
				headers['Authorization'] = `Bearer ${this.token}`
				response = await fetch(`${API_URL}${endpoint}`, {
					method: options.method || 'GET',
					headers,
					body: reqBody ? JSON.stringify(reqBody) : undefined,
					signal: options.signal,
				})
			} else {
				window.location.href = '/login'
				throw new Error('Сессия истекла')
			}
		}

		if (!response.ok) {
			const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
			// detail — FastAPI-исключения; error — наши {ok:false, error} ручки (pipelines и др.)
			const thrown = new Error(
				russifyError(error.detail || error.error || 'Request failed'),
			) as Error & { detail?: unknown; status?: number; captchaRequired?: boolean; payload?: unknown }
			// Прикладываем исходный detail: у части ручек это ОБЪЕКТ {code,message}
			// (контракт персонажей), и russifyError превращал его в безликое
			// «Ошибка сервера» — юзер не видел, что именно не так.
			thrown.detail = error.detail
			thrown.status = response.status
			// Полный ответ ручек {ok:false, error:<code>, …extra}: флоу кладут в extra
			// total/balance/nodes/problems — без них UI не объяснит отказ.
			thrown.payload = error
			// Turnstile: бэк шлёт этот заголовок на 403 непройденной капчи И на
			// 401 логина, когда счётчик неудач достиг порога (капча понадобится
			// на СЛЕДУЮЩЕЙ попытке) — см. api/v1/auth.py:login. expose_headers
			// на бэке обязателен, иначе браузер не отдаёт кастомный заголовок JS.
			thrown.captchaRequired = response.headers.get('X-Captcha-Required') === '1'
			// Не хватает токенов — единый вид для любой ручки (utils/credits): текст с суммами сервера, тост с «Пополнить»,
			// свежий баланс; свой тост места вызова о том же гасит showToast.
			if (isInsufficientError(thrown)) {
				thrown.message = insufficientText(thrown, thrown.message)
				reportInsufficientCredits(thrown.message, { silent: !!options.silentInsufficient })
			}
			throw thrown
		}

		// 204 No Content (и любой пустой ответ): парсить нечего. DELETE-ручки
		// отдают именно 204 — response.json() бросал там «Unexpected end of JSON
		// input», и успешное удаление показывалось юзеру как ошибка сервера.
		if (response.status === 204) return undefined as T
		const raw = await response.text()
		if (!raw) return undefined as T
		const parsed: unknown = JSON.parse(raw)
		// Ручки с телом {ok:false, error:«Недостаточно токенов…»} (музыка, озвучка, чат) — тот же стандартный тост;
		// тело не трогаем: места вызова разбирают свои коды.
		const body = parsed as { ok?: unknown; error?: unknown } | null
		if (body && typeof body === 'object' && body.ok === false && isInsufficientMessage(body.error)) {
			reportInsufficientCredits(insufficientText(parsed), { silent: !!options.silentInsufficient })
		}
		return parsed as T
	}

	async streamRequest(
		endpoint: string,
		body: unknown,
		onChunk: (text: string) => void,
	): Promise<void> {
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
		}

		if (this.token) {
			headers['Authorization'] = `Bearer ${this.token}`
		}

		const response = await fetch(`${API_URL}${endpoint}`, {
			method: 'POST',
			headers,
			body: JSON.stringify(body),
		})

		if (!response.ok) {
			const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
			// detail — FastAPI-исключения; error — наши {ok:false, error} ручки (pipelines и др.)
			const thrown = Object.assign(new Error(russifyError(error.detail || error.error || 'Request failed')),
				{ status: response.status, detail: error.detail, payload: error })
			if (isInsufficientError(thrown)) {
				thrown.message = insufficientText(thrown, thrown.message)
				reportInsufficientCredits(thrown.message)
			}
			throw thrown
		}

		const reader = response.body?.getReader()
		if (!reader) throw new Error('Нет данных')

		const decoder = new TextDecoder()

		while (true) {
			const { done, value } = await reader.read()
			if (done) break

			const chunk = decoder.decode(value, { stream: true })
			const lines = chunk.split('\n')

			for (const line of lines) {
				if (line.startsWith('data: ')) {
					const data = line.slice(6)
					if (data === '[DONE]') return
					try {
						const parsed = JSON.parse(data)
						if (parsed.content) {
							onChunk(parsed.content)
						}
					} catch {
						if (data.trim()) {
							onChunk(data)
						}
					}
				}
			}
		}
	}
}

export const api = new ApiClient()
export default api
